import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

export default function BestTimeToBuildDeckCalgary() {
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const sidebarImages = [
    { src: "/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg", alt: "TimberTech Terrain composite deck Calgary" },
    { src: "/assets/images/Decks/Composite Decks/Tigerwood-and-mocha-2.jpg", alt: "TimberTech Tigerwood composite deck Calgary" },
    { src: "/assets/images/Decks/Wood Decks/Large-outdoor-seating-area-with-Custom-Cedar-privacy-wall.jpg", alt: "Cedar deck with privacy wall Calgary" },
    { src: "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg", alt: "Multi-level TimberTech deck with LED lighting Calgary" },
    { src: "/assets/images/Decks/Wood Decks/Multi-level-Cedar-wood-deck.jpg", alt: "Multi-level cedar wood deck Calgary" },
    { src: "/assets/images/Decks/Composite Decks/Timbertech-Legacy-covered-deck-in-Tigerwood-surface-and-Pecan-skirting.jpg", alt: "Covered TimberTech Legacy deck Calgary" },
    { src: "/assets/images/Decks/Composite Decks/Timbertech-deck-with-PT-wood-skirting-for-underdeck-storage.jpg", alt: "Composite deck with underdeck storage Calgary" },
    { src: "/assets/images/Decks/Wood Decks/Custom-roof-covered-backyard-Cedar-deck-with-wrap-around-steps.jpg", alt: "Covered cedar deck with wrap-around steps Calgary" },
  ]

  // Consolidated Schema Data for Best Time to Build a Deck in Calgary
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": "Best Time to Build a Deck in Calgary",
        "description": "The best months to build a deck in Calgary are May through June and August through October. Learn how to beat the booking queue and plan your custom build.",
        "image": "https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg",
        "author": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "url": "https://busybeaverconstruction.com/"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "logo": {
            "@type": "ImageObject",
            "url": "https://busybeaverconstruction.com/assets/images/2020/03/busy-beaver-logo.png"
          }
        },
        "datePublished": "2023-09-12T00:00:00+00:00",
        "dateModified": "2023-09-12T00:00:00+00:00",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://busybeaverconstruction.com/blog/best-time-to-build-deck-calgary"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://busybeaverconstruction.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Best Time to Build a Deck in Calgary",
            "item": "https://busybeaverconstruction.com/blog/best-time-to-build-deck-calgary"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is the best month to build a deck in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "May and June give you the best conditions and the most time to enjoy the deck through summer. September is the best month if you want shorter wait times and better contractor availability. Both are strong options depending on whether your priority is enjoying the deck this summer or getting it done without the peak-season hassle."
            }
          },
          {
            "@type": "Question",
            "name": "Can you build a deck in Calgary in winter?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Some work is possible during chinook windows, but footing work cannot be done while the ground is frozen at depth. Most residential deck projects in Calgary require new footings, which means meaningful construction cannot start until the frost line thaws, typically late April in a normal year. Winter is the right time to plan, design, get estimates, and submit permit applications so you are ready to start the moment ground conditions allow."
            }
          },
          {
            "@type": "Question",
            "name": "How far in advance should I book a Calgary deck contractor?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "For a spring or early summer start, book in February or March. For a fall build, June or July is fine. The closer you get to May without a contractor confirmed, the more likely you are looking at a late summer build at best."
            }
          },
          {
            "@type": "Question",
            "name": "Does it take longer to get a deck permit in spring in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The City of Calgary targets 7 to 10 business days for a complete residential deck permit application year-round. However, spring sees higher application volumes, and incomplete submissions that require back-and-forth with the City can stretch that timeline. Fall applications tend to move faster in practice. Either way, submit your permit application as early as possible — do not wait until you have a confirmed build date."
            }
          },
          {
            "@type": "Question",
            "name": "Is it cheaper to build a deck in fall or winter in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Fall can offer modest savings on contractor scheduling, and locking in material pricing before spring demand cycles can help. A signed contract in fall protects you from price increases that often come in late winter as suppliers prepare for spring. Winter itself is not a viable build period for most projects, so the cost conversation in winter is about planning and contracting, not building."
            }
          },
          {
            "@type": "Question",
            "name": "Can composite decking be installed in cold weather in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Most composite manufacturers specify a minimum installation temperature because board expansion gaps are calculated based on install-time temperature. Installing composite boards in cold conditions without adjusting gap spacing leads to buckling in summer heat. As a TimberTech Platinum Pro Builder, we follow manufacturer guidelines on installation temperature without exception."
            }
          }
        ]
      }
    ]
  };

  return (
    <main className="bg-gray-50">
      <Helmet>
        <title>Best Time to Build a Deck in Calgary | Busy Beaver Construction</title>
        <meta name="description" content="The best months to build a deck in Calgary are May through June and August through October. Learn how to beat the booking queue and plan your custom build." />
        <meta name="keywords" content="best time to build deck Calgary, deck contractor booking Calgary, Calgary deck building season, winter deck building Calgary" />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog/best-time-to-build-deck-calgary" />
        <meta property="og:title" content="Best Time to Build a Deck in Calgary | Busy Beaver Construction" />
        <meta property="og:description" content="Find out the best months to build your custom deck in Calgary, how weather impacts construction, and when to book your contractor." />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg" as="image" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />

      {/* Hero */}
      <section className="relative h-[500px] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/55 z-10"></div>
        <img
          src="/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg"
          alt="Custom deck build in Calgary by Busy Beaver Construction"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
        />
        <div className="relative z-20 mt-32 text-center max-w-4xl mx-auto px-4">
          <div className="inline-block bg-beaver-green rounded py-2 px-6 mb-6">
            <p className="text-black text-sm font-bold tracking-wider uppercase">Planning Guide</p>
          </div>
          <h1 className="text-white text-5xl lg:text-7xl font-primary drop-shadow-lg mb-4 leading-tight">
            Best Time to Build a Deck in Calgary
          </h1>
          <p className="text-white/90 text-lg font-medium font-accent mt-4">
            And How to Beat the Booking Queue
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

            {/* Article */}
            <article className="lg:col-span-8 space-y-8 text-lg text-gray-700 leading-relaxed font-text">

              <p className="text-xl text-gray-900 leading-relaxed font-bold">
                The best months to build a deck in Calgary are May through June and August through October. Spring gives you the longest window to enjoy the finished deck. Fall gets you shorter wait times, faster permits, and often better pricing.
              </p>

              <p>
                Most Calgary homeowners call a deck contractor in April or May expecting to be on site within a few weeks. That is not how it works during peak season. By the time spring arrives, most reputable contractors are already booked 6 to 10 weeks out. Your April call becomes a July build, and your July build runs into fall weather risk.
              </p>

              <p>
                Timing a deck project in Calgary is about more than picking a warm month. Ground conditions, permit processing times, contractor availability, and material lead times all affect when your deck actually gets built. This guide covers all of it.
              </p>

              <p className="italic text-gray-500">
                From Busy Beaver Construction, Calgary's longest-running deck builder since 1999.
              </p>

              {/* Short Answer */}
              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                The Short Answer: Best Months to Build a Deck in Calgary
              </h2>

              <p>
                If you want the summary before the detail, here it is.
              </p>

              <div className="overflow-x-auto my-8 shadow-sm rounded-lg border border-gray-200">
                <table className="w-full text-left border-collapse text-base">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="p-4 font-bold font-secondary">Month</th>
                      <th className="p-4 font-bold font-secondary">Build Conditions</th>
                      <th className="p-4 font-bold font-secondary">Contractor Availability</th>
                      <th className="p-4 font-bold font-secondary">Overall</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-base">
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="p-4 font-medium">January / February</td>
                      <td className="p-4 text-gray-600">Poor — ground frozen</td>
                      <td className="p-4 text-gray-600">High</td>
                      <td className="p-4 text-gray-600">Planning only</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="p-4 font-medium">March</td>
                      <td className="p-4 text-gray-600">Poor to fair — still freezing</td>
                      <td className="p-4 text-gray-600">High</td>
                      <td className="p-4 text-gray-600">Good time to book</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="p-4 font-medium">April</td>
                      <td className="p-4 text-gray-600">Fair — ground thawing</td>
                      <td className="p-4 text-gray-600">Filling fast</td>
                      <td className="p-4 text-gray-600">Book immediately</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="p-4 font-medium">May</td>
                      <td className="p-4 text-gray-600">Good</td>
                      <td className="p-4 text-gray-600">Low — queue full</td>
                      <td className="p-4 text-gray-600">Good if pre-booked</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="p-4 font-medium">June</td>
                      <td className="p-4 text-gray-600">Excellent</td>
                      <td className="p-4 text-gray-600">Very low</td>
                      <td className="p-4 text-gray-600">Best conditions, hardest to book</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="p-4 font-medium">July</td>
                      <td className="p-4 text-gray-600">Excellent</td>
                      <td className="p-4 text-gray-600">Very low</td>
                      <td className="p-4 text-gray-600">Peak — fully booked</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="p-4 font-medium">August</td>
                      <td className="p-4 text-gray-600">Excellent</td>
                      <td className="p-4 text-gray-600">Low — improving</td>
                      <td className="p-4 text-gray-600">Strong option</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="p-4 font-medium">September</td>
                      <td className="p-4 text-gray-600">Very good</td>
                      <td className="p-4 text-gray-600">Moderate</td>
                      <td className="p-4 text-gray-600">Underrated — highly recommended</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-50">
                      <td className="p-4 font-medium">October</td>
                      <td className="p-4 text-gray-600">Fair — weather dependent</td>
                      <td className="p-4 text-gray-600">Good</td>
                      <td className="p-4 text-gray-600">Viable with timing</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="p-4 font-medium">November / December</td>
                      <td className="p-4 text-gray-600">Poor</td>
                      <td className="p-4 text-gray-600">High</td>
                      <td className="p-4 text-gray-600">Plan and deposit only</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Calgary Climate */}
              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                Why Calgary's Climate Narrows the Build Window
              </h2>

              <p>
                Calgary gets more sunny days than Vancouver or Toronto, but the usable build season is shorter than most people expect. The issue is not just winter cold. It is the specific combination of late spring frost risk, a frozen frost line that runs deep, and concrete curing requirements that create hard stops at both ends of the season.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Ground Freeze and Footing Installation
              </h3>
              <p>
                Calgary's frost line sits at roughly 1.2 to 1.5 metres below grade, one of the deepest in major Canadian cities. Every deck that requires a building permit needs footings poured to that depth. In a cold year, the ground at that depth can stay frozen well into April. You cannot drill and pour footings into frozen ground — the hole collapses and the concrete will not set properly. This is why early April starts are rarely possible even when the surface looks clear.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Concrete Curing Temperature Requirements
              </h3>
              <p>
                Concrete needs a minimum of 5°C to cure correctly, and it needs to hold that temperature for several days after the pour. In Calgary, overnight temperatures below 5°C are common through May and return by mid-October. A footing poured when overnight temps are borderline will cure slowly or incompletely, which affects the structural integrity of everything built on top of it. Experienced Calgary contractors watch the 14-day forecast before scheduling concrete work, not just the day-of temperature.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Composite Decking Installation Temperature Minimums
              </h3>
              <p>
                Composite decking boards expand and contract with temperature. Most manufacturers, including <a href="/blog/timbertech-vs-trex-calgary" onClick={(e) => handleNavigation(e, '/blog/timbertech-vs-trex-calgary')} className="text-beaver-green font-semibold hover:underline">TimberTech</a>, specify installation temperature minimums because the gap spacing between boards is calculated for the temperature at time of install. Boards installed in cold weather are contracted. If gaps are set for cold-install conditions, the boards can buckle against each other during summer heat expansion. As a <strong>TimberTech Platinum Pro Builder</strong>, we follow manufacturer installation guidelines strictly, which means composite work stops when temperatures drop below the specified threshold.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Chinooks and Unexpected Winter Windows
              </h3>
              <p>
                Calgary's chinooks occasionally create short warm windows in January and February where surface temperatures climb above 10°C for several consecutive days. Some framing and decking work is technically possible during these windows, but footing work is not because the ground at depth stays frozen regardless of the surface temperature. Chinook windows are useful for getting a head start on a project already permitted and framed, not for starting from scratch.
              </p>

              {/* Booking Reality */}
              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                The Booking Reality: Calgary Contractor Wait Times
              </h2>

              <p>
                This is the part most homeowners do not find out until it is too late. Calgary has a relatively small number of quality, established deck builders relative to the demand that hits every spring. When the snow melts, the phones ring from every direction at once. The contractors who do good work fill up quickly and the ones with open slots in June often have open slots for a reason.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Spring Booking Queue
              </h3>
              <p>
                By mid-April, most established Calgary deck contractors are booking 6 to 10 weeks out. That means a call in late April puts your project start date in late June at the earliest, assuming permits are already in hand. If permits are not yet applied for, add another 1 to 3 weeks to that timeline. A call in May typically means a July or August start. This is not a complaint about contractors being slow. It is the reality of a short build season with compressed demand.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                How Permit Timelines Affect Your Schedule
              </h3>
              <p>
                The City of Calgary processes most residential deck building permit applications within 7 to 10 business days when the application is complete. If anything is missing or needs clarification, that clock resets. A development permit, which is required in certain setback or front-yard situations, adds 10 to 12 weeks to the timeline. Read our full breakdown of <a href="/blog/4" onClick={(e) => handleNavigation(e, '/blog/4')} className="text-beaver-green font-semibold hover:underline">Calgary deck permit requirements</a> to understand which category your project falls into before you start booking.
              </p>
              <p>
                The practical takeaway: if your project needs a permit, that application should go in before you are even thinking about a build start date. Waiting until a contractor is confirmed before starting the permit process loses you 2 to 4 weeks you cannot recover.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                How Far in Advance Should You Contact a <a href="/" onClick={(e) => handleNavigation(e, '/')} className="text-beaver-green hover:underline">Deck Builder in Calgary</a>
              </h3>
              <p>
                For a spring or early summer build, contact contractors in February or March. That gives you time to get estimates, make a decision, confirm a design, and get permits submitted before the May rush. For a fall build, June or July contact is sufficient. You will have more contractor options, less timeline pressure, and in some cases better pricing on materials because suppliers are not in peak demand.
              </p>

              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-beaver-green my-8">
                <p className="text-gray-700">
                  <strong>From experience:</strong> The customers who end up happiest with their deck projects are the ones who start the conversation in winter. They get first pick of build dates, have time to think through the design properly, and are not making rushed decisions because a contractor has a slot opening in two weeks.
                </p>
              </div>

              {/* Why Fall */}
              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                Why Fall Is the Best-Kept Secret for Calgary Deck Builds
              </h2>

              <p>
                Most homeowners think about decks in spring and summer. That thinking is understandable but it works against you in practical terms. A September or October deck build has several real advantages over a June or July one.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Shorter Wait Times
              </h3>
              <p>
                By August and September, contractors are completing the projects they booked in spring. The queue shortens considerably. Where a May call might get you a July start, an August call can often get you a September start. The gap between contact and build date compresses significantly.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Better Working Conditions
              </h3>
              <p>
                Calgary in July is hot. Crews working in direct sun on exposed deck framing are dealing with temperatures that affect both comfort and productivity. September builds happen in cooler, more consistent conditions. Wood is more stable in moderate temperatures, composite installation gap calculations are more reliable, and concrete pours are less at risk from rapid moisture evaporation.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Lock In Pricing Before Spring Increases
              </h3>
              <p>
                Lumber and composite decking prices follow demand cycles. Prices tend to climb in the lead-up to spring as distributors anticipate the season. A fall build locks in current pricing. Signing a contract in September or October on a project scheduled for completion before freeze-up, or one that will start the following spring with a confirmed price, protects you from material cost movement.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Ready for Spring the Moment the Weather Turns
              </h3>
              <p>
                A deck finished in late September or October sits ready through winter. The moment Calgary gets its first genuinely warm weekend in April or May, your deck is there. Contrast that with a May booking that starts your build in July — you lose the best part of the Calgary summer waiting for construction to finish.
              </p>

              {/* How Long */}
              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                How Long Does It Take to Build a Deck in Calgary
              </h2>

              <p>
                The build time itself is shorter than most homeowners expect. The total project timeline from first call to finished deck is longer than most expect. Understanding both numbers helps you plan.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Actual Build Time on Site
              </h3>
              <ul className="list-disc pl-6 space-y-4 marker:text-beaver-green">
                <li><strong>Small deck under 200 sq ft, simple design, ground level:</strong> 2 to 3 days on site.</li>
                <li><strong>Medium deck 200 to 400 sq ft with railing and stairs:</strong> 4 to 7 days on site.</li>
                <li><strong>Large or multi-level deck over 400 sq ft:</strong> 1 to 3 weeks on site depending on complexity, pergola inclusion, or custom features like built-in seating or lighting.</li>
              </ul>
              <p className="mt-4">
                These are build days, not calendar days. Weather delays, material delivery scheduling, and inspection holds can add days between work sessions. A deck that takes 5 build days might span 8 to 10 calendar days.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Total Project Timeline From First Call to Completion
              </h3>
              <ul className="list-disc pl-6 space-y-4 marker:text-beaver-green">
                <li><strong>No permit required, off-peak season:</strong> 3 to 5 weeks from first call to finished deck.</li>
                <li><strong>Permit required, off-peak season:</strong> 5 to 8 weeks from first call to finished deck.</li>
                <li><strong>Permit required, peak season (May to July):</strong> 8 to 14 weeks from first call to finished deck.</li>
                <li><strong>Development permit required, any season:</strong> Add 10 to 12 weeks to any of the above.</li>
              </ul>

              <p className="mt-4">
                The project timeline for a deck that needs a full <a href="/blog/5" onClick={(e) => handleNavigation(e, '/blog/5')} className="text-beaver-green font-semibold hover:underline">detailed estimate and design</a> is longer than a straightforward replacement. If you know exactly what you want and the site is straightforward, timelines compress. If the design involves multiple levels, angles, or attached structures, plan for the longer end of each range.
              </p>

              {/* What causes delays */}
              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                What Causes Delays and How to Avoid Them
              </h2>

              <ul className="list-disc pl-6 space-y-4 marker:text-beaver-green">
                <li>
                  <strong>Incomplete permit applications.</strong> The City will not process an application that is missing documents. Every incomplete submission resets your wait time. Work with a contractor who prepares permit submissions regularly and knows what Calgary requires.
                </li>
                <li>
                  <strong>Late design decisions.</strong> Changing the deck layout, material, or railing type after a contract is signed can push your start date back by weeks if material orders need to be revised.
                </li>
                <li>
                  <strong>Material lead times for specialty products.</strong> Standard pressure-treated lumber and basic composite lines are typically in stock. Custom TimberTech colours, specialty railing systems, or glass panels may have lead times of 2 to 4 weeks. This needs to be built into your schedule before the build start date, not discovered during framing.
                </li>
                <li>
                  <strong>Waiting to call.</strong> The single most common cause of a delayed or missed-season deck project is the homeowner who starts making calls in May expecting June availability. Contact contractors in late winter if a spring or summer build matters to you.
                </li>
              </ul>

              {/* Getting Started */}
              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                How to Get Your Project Started at the Right Time
              </h2>

              <p>
                The process at Busy Beaver starts with a free on-site estimate. We come to your property, assess the site, discuss what you want, and give you a detailed written quote. That meeting takes about an hour and covers material options, design possibilities, and a realistic timeline based on our current schedule.
              </p>
              <p>
                From there, if you want to proceed, we handle the permit application and all City submissions. You do not need to manage that process yourself. We have been through it hundreds of times and know what Calgary's development and building departments need.
              </p>
              <p>
                Financing is available through our Financeit partnership if you want to spread the cost. A new deck does not have to mean a large lump-sum outlay. Monthly payment options start from $99 depending on the project scope.
              </p>
              <p>
                If you are thinking about a deck for next spring, the right time to start this conversation is now, not in March when everyone else picks up the phone at the same time.
              </p>

              {/* Dark CTA */}
              <div className="bg-gray-900 text-white rounded-xl p-8 my-12 shadow-xl border-l-4 border-beaver-green">
                <h2 className="text-2xl font-bold mb-4 font-secondary text-beaver-green">
                  Book Before the Spring Rush
                </h2>
                <p className="mb-4 text-gray-300">
                  We book 6 to 10 weeks out once spring arrives. If you want a May or June build, the time to call is now. A free on-site estimate costs you nothing and locks in your place in the schedule.
                </p>
                <p className="text-gray-300 mb-6">
                  Call us at <strong>(403) 286-5400</strong> or book your estimate below. We serve all Calgary neighbourhoods and surrounding areas.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="tel:4032865400" className="inline-flex justify-center items-center px-6 py-3 bg-white text-black font-bold rounded shadow-lg hover:bg-gray-100 transition-all font-accent">
                    Call (403) 286-5400
                  </a>
                  <a href="/appointment" onClick={(e) => handleNavigation(e, '/appointment')} className="inline-flex justify-center items-center px-6 py-3 bg-beaver-green text-black font-bold rounded shadow-lg hover:brightness-110 transition-all font-accent">
                    Book Free Estimate
                  </a>
                </div>
              </div>

              {/* FAQ */}
              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                FAQ: Timing a Deck Build in Calgary
              </h2>

              <div className="space-y-8">
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">What is the best month to build a deck in Calgary?</h4>
                  <p>May and June give you the best conditions and the most time to enjoy the deck through summer. September is the best month if you want shorter wait times and better contractor availability. Both are strong options depending on whether your priority is enjoying the deck this summer or getting it done without the peak-season hassle.</p>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Can you build a deck in Calgary in winter?</h4>
                  <p>Some work is possible during chinook windows, but footing work cannot be done while the ground is frozen at depth. Most residential deck projects in Calgary require new footings, which means meaningful construction cannot start until the frost line thaws, typically late April in a normal year. Winter is the right time to plan, design, get estimates, and submit permit applications so you are ready to start the moment ground conditions allow.</p>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">How far in advance should I book a Calgary deck contractor?</h4>
                  <p>For a spring or early summer start, book in February or March. For a fall build, June or July is fine. The closer you get to May without a contractor confirmed, the more likely you are looking at a late summer build at best.</p>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Does it take longer to get a deck permit in spring in Calgary?</h4>
                  <p>The City of Calgary targets 7 to 10 business days for a complete residential deck permit application year-round. However, spring sees higher application volumes, and incomplete submissions that require back-and-forth with the City can stretch that timeline. Fall applications tend to move faster in practice. Either way, submit your permit application as early as possible — do not wait until you have a confirmed build date.</p>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Is it cheaper to build a deck in fall or winter in Calgary?</h4>
                  <p>Fall can offer modest savings on contractor scheduling, and locking in material pricing before spring demand cycles can help. A signed contract in fall protects you from price increases that often come in late winter as suppliers prepare for spring. Winter itself is not a viable build period for most projects, so the cost conversation in winter is about planning and contracting, not building.</p>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Can composite decking be installed in cold weather in Calgary?</h4>
                  <p>Most composite manufacturers specify a minimum installation temperature because board expansion gaps are calculated based on install-time temperature. Installing composite boards in cold conditions without adjusting gap spacing leads to buckling in summer heat. As a TimberTech Platinum Pro Builder, we follow manufacturer guidelines on installation temperature without exception. This is one reason fall builds need to be timed carefully to complete composite work before consistent overnight freezing begins.</p>
                </div>
              </div>

              {/* Related links */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4 font-secondary">Related Reading</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>
                    <span className="text-beaver-green font-bold mr-2">→</span>
                    <a href="/blog/5" onClick={(e) => handleNavigation(e, '/blog/5')} className="text-beaver-green font-semibold hover:underline">How much does a deck cost in Calgary?</a>
                  </li>
                  <li>
                    <span className="text-beaver-green font-bold mr-2">→</span>
                    <a href="/blog/4" onClick={(e) => handleNavigation(e, '/blog/4')} className="text-beaver-green font-semibold hover:underline">Calgary deck permit requirements</a>
                  </li>
                  <li>
                    <span className="text-beaver-green font-bold mr-2">→</span>
                    <a href="/blog/calgary-deck-maintenance-checklist" onClick={(e) => handleNavigation(e, '/blog/calgary-deck-maintenance-checklist')} className="text-beaver-green font-semibold hover:underline">Calgary deck maintenance checklist</a>
                  </li>
        
                  <li>
                    <span className="text-beaver-green font-bold mr-2">→</span>
                    <a href="/blog/deck-replacement-vs-repair-calgary" onClick={(e) => handleNavigation(e, '/blog/deck-replacement-vs-repair-calgary')} className="text-beaver-green font-semibold hover:underline">Deck replacement vs repair Calgary</a>
                  </li>
                  <li>
                    <span className="text-beaver-green font-bold mr-2">→</span>
                    <a href="/blog/types-of-decks-calgary" onClick={(e) => handleNavigation(e, '/blog/types-of-decks-calgary')} className="text-beaver-green font-semibold hover:underline">Types of decks in Calgary</a>
                  </li>
                </ul>
              </div>

            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold mb-6 font-secondary text-gray-900">Recent Calgary Projects</h3>
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 max-h-[700px] overflow-y-auto pr-2">
                  {sidebarImages.map((img, index) => (
                    <div key={index} className="overflow-hidden rounded-lg h-48 lg:h-56 group shadow-sm">
                      <img
                        src={img.src}
                        alt={img.alt}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-600 mb-4 font-text">
                    25 years building Calgary decks. Free on-site estimates with no obligation.
                  </p>
                  <a href="/appointment" onClick={(e) => handleNavigation(e, '/appointment')} className="block text-center bg-beaver-green text-black font-bold py-3 px-4 rounded hover:brightness-110 transition-all font-accent">
                    Book Free Estimate
                  </a>
                  <a href="tel:4032865400" className="block text-center text-beaver-red font-bold mt-3 hover:underline font-accent">
                    (403) 286-5400
                  </a>
                </div>
              </div>
            </aside>

          </div>
        </div>
      </section>

      <Footer />
    </main>
  )
}